/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import moment from 'moment';
import { DATALAYER_CONSTANT } from '../../constants/lib/constants';
import { logger } from '../../utils/src/logger';
import { KeysRequest } from '../../utils/src/dataLayer/index.types';
import Cookies from 'js-cookie';
import { addSubDirectoryPrefix } from '../../utils/src/subDirectoryFunctions';
import { DE_LOCALE, ES_LOCALE, FR_LOCALE, PT_BR_LOCALE, RU_LOCALE } from '../constants';
import { EXCLUSION_CRITERIA_DESTINATION_TYPE } from '../../organisms/SearchResults/lib/application.constants';
import { DateObjectType, getLocalizedDate } from '@marriott/mi-ui-library';
import { isCNLocale, isGreaterChinaLocale } from '@marriott/shared/mi-helper-utils';
import { MAP_VIEW_SESSION_KEY } from '../store/store.constants';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';

const { log } = logger({})('AvailabilityCalendar/helper');

export const getBreakpoint = (): string | undefined => {
  if (typeof window !== 'undefined' && window?.screen && window?.screen?.width) {
    return window.innerWidth > 991 ? 'desktop' : window.innerWidth > 767 ? 'tablet' : 'mobile';
  } else {
    return undefined;
  }
};

export const getClassForBreakpoint = (
  breakpoint: string | undefined,
  desktopClass: string,
  tabletClass: string,
  mobileClass: string
): string => {
  if (!breakpoint) return desktopClass;
  if (breakpoint === 'tablet') {
    return tabletClass;
  } else if (breakpoint === 'mobile') {
    return mobileClass;
  } else return desktopClass;
};

export async function updateDataLayerOnInteractions(
  resolvedUrl: string,
  headersData: any,
  query: any,
  datalayerParams: any,
  currentLocale: string,
  sessionID: string,
  propertyId?: string,
  fusionSearchId?: string,
  isBulgariDomain = false,
  searchFallbackExistsCustomerCall?: boolean,
  searchFallbackTypeCustomerCall?: string,
  clusterCode?: string
) {
  const {
    useridtoken,
    mi_site,
    mi_visitor,
    remembermeflag = false,
    remembermeuserid = '',
    remembermealternateid = '',
    referer,
  } = headersData;
  const getPageURI = resolvedUrl ? resolvedUrl.split('?')[0] : '';
  const getPageReferrer = referer ? referer.split('?')[0] : '';
  const MOCK_SESSION_ID = '825167AA-9F54-5B1B-95C0-53EEC4E22391';
  const absolutePageURL = `${getPageURI}.mi`;
  const channelValue = datalayerParams?.NEXT_PUBLIC_DATALAYER_SITENAME?.split('.')[0];
  const localeKeyValue = currentLocale?.replace('_', '-') ?? 'en-US';
  const datalayerUrl = isBulgariDomain
    ? datalayerParams?.MI_DATALAYER_APP_URL
    : addSubDirectoryPrefix(datalayerParams?.MI_DATALAYER_APP_URL);

  const getHeaders = (): string => {
    let cookieValues = '';
    if (mi_site) {
      cookieValues = cookieValues + ` MI_SITE=${mi_site};`;
    }
    if (mi_visitor) {
      cookieValues = cookieValues + ` MI_VISITOR=${mi_visitor};`;
    }
    if (remembermeuserid) {
      cookieValues = cookieValues + ` RememberMeUserID=${encodeURIComponent(remembermeuserid)};`;
    }
    if (remembermealternateid) {
      cookieValues = cookieValues + ` RememberMeAlternateID=${encodeURIComponent(remembermealternateid)};`;
    }
    if (useridtoken) {
      cookieValues = cookieValues + ` UserIdToken=${encodeURIComponent(useridtoken)};`;
    }
    return cookieValues;
  };

  const headersKey: Record<string, string | undefined> = {};

  // creating headers for datalayer call
  if (headersData && Object.keys(headersData).length > 0) {
    DATALAYER_CONSTANT.headerKeys.forEach(name => {
      if (name && headersData[name as KeysRequest['keys']]) {
        headersKey[name] = headersData[name as KeysRequest['keys']];
      }
    });
  }

  const reqBody: any = {
    context: {
      localeKey: localeKeyValue,
      programFlag: '',
      siteName: datalayerParams?.NEXT_PUBLIC_DATALAYER_SITENAME,
      mobileAuthEnabled: 'false',
      productSiteId: datalayerParams?.NEXT_PUBLIC_DATALAYER_PRODUCT,
      channel: channelValue,
      pageContent: [],
      pageURI: getPageURI,
      referer: getPageReferrer,
      absolutePageURL: absolutePageURL,
      applicationName: datalayerParams?.NEXT_PUBLIC_DATALAYER_APPLICATION,
      products: datalayerParams?.NEXT_PUBLIC_DATALAYER_PRODUCT,
      template: datalayerParams?.NEXT_PUBLIC_DATALAYER_TEMPLATE,
      propertyId: propertyId,
      marshaCode: propertyId,
      seoQueryParams: query,
      fusionSearchId: fusionSearchId,
      isDtt: true,
    },
    sessionToken: sessionID ?? MOCK_SESSION_ID,
    sourceURI: absolutePageURL,
    variation: datalayerParams?.NEXT_PUBLIC_DATALAYER_VARIATION,
  };

  if (searchFallbackExistsCustomerCall === false) {
    reqBody.context.isSearchFallback = searchFallbackExistsCustomerCall;
    reqBody.context.searchFallbackType = searchFallbackTypeCustomerCall;
  }

  // datalayer call
  const dataLayer = await axios
    .post(datalayerUrl, reqBody, {
      headers: {
        // please make sure these coookies are in single line. Else there will be `TypeError [ERR_INVALID_CHAR]`
        cookie: `sessionID=${sessionID}; RememberMeFlag=${remembermeflag}; ${getHeaders()}`,
        'X-Host': typeof window !== 'undefined' ? window.location.host : '',
        'x-request-id': useClientEnvVarsStore.getState().envVarsObject['REQUEST_ID'],
        ...headersKey,
      },
    })
    .catch(err => {
      log.error(
        `[${resolvedUrl}] [DataLayer] Aries API Failed: ${err?.response?.status} [message]: ${err?.response?.data}`
      );
    });

  if (clusterCode !== 'none' && !window?.dataLayer?.['search_cluster_code'] && window.dataLayer) {
    window.dataLayer['search_cluster_code'] = clusterCode;
  }

  if (window?.dataLayer?.['search_cluster_code'] && clusterCode && clusterCode === 'none') {
    delete window.dataLayer['search_cluster_code'];
  }

  const datalayerResponse = dataLayer && dataLayer?.data?.component?.data?.dataProperties;
  if (datalayerResponse?.length && window?.dataLayer !== undefined) {
    for (const [key] of Object.entries(window.dataLayer)) {
      datalayerResponse.forEach((entry: any) => {
        if (window && window.dataLayer) {
          if (key === entry.key) {
            window.dataLayer[key] = entry.value;
          } else {
            window.dataLayer[entry.key] = entry.value;
          }
        }
      });
    }
  }
  // Set the required dataLayer attributes
  if (window && window.dataLayer) {
    window.dataLayer['page_data_layer_ready'] = 'true';
  }
}

/**
 * Function to determine the screen size's
 */

export function getScreenCategory() {
  let screenCategory = '';
  if (window.innerWidth < 240) {
    screenCategory = 'XXS';
  } else if (window.innerWidth >= 240 && window.innerWidth < 320) {
    screenCategory = 'XS';
  } else if (window.innerWidth >= 320 && window.innerWidth < 480) {
    screenCategory = 'S';
  } else if (window.innerWidth >= 480 && window.innerWidth < 560) {
    screenCategory = 'SM';
  } else if (window.innerWidth >= 560 && window.innerWidth < 672) {
    screenCategory = 'M';
  } else if (window.innerWidth >= 672 && window.innerWidth < 768) {
    screenCategory = 'MML';
  } else if (window.innerWidth >= 768 && window.innerWidth < 864) {
    screenCategory = 'ML';
  } else if (window.innerWidth >= 864 && window.innerWidth < 960) {
    screenCategory = 'MLL';
  } else if (window.innerWidth >= 960 && window.innerWidth < 1024) {
    screenCategory = 'L';
  } else if (window.innerWidth >= 1024 && window.innerWidth < 1200) {
    screenCategory = 'XL';
  } else if (window.innerWidth >= 1200 && window.innerWidth < 1440) {
    screenCategory = 'XXL';
  } else if (window.innerWidth >= 1440) {
    screenCategory = 'XXXL';
  } else {
    screenCategory = 'Unknown';
  }
  return screenCategory;
}

/**
 * updating and adding the datalayer attributes
 * */

export function updateDataLayerObj(dataLayer: Record<string, unknown>, datalayerProperties: any) {
  const deviceOrientation = datalayerProperties?.NEXT_PUBLIC_ORIENTATION_LANDSCAPE;
  const screenCategory = getScreenCategory();
  let pathType = '';
  if (Cookies.get('newSearch')) {
    pathType = datalayerProperties?.NEXT_PUBLIC_PATHTYPE_NEW ?? '';
  } else {
    pathType = datalayerProperties?.NEXT_PUBLIC_PATHTYPE_LEGACY ?? '';
  }
  dataLayer['ram_tile_names'] = datalayerProperties?.NEXT_PUBLIC_RAM_TILE_NAME;
  dataLayer['page_type'] = window.location?.href;
  dataLayer['ensightenEnabled'] = true;
  dataLayer['previous_page'] = window?.document?.referrer;
  dataLayer['search_multiRate_or_single'] = datalayerProperties?.NEXT_PUBLIC_SEARCH_SINGLE_RATE ?? '';
  dataLayer['search_res_path_type'] = pathType;
  dataLayer[
    'page_breakpt_orientation'
  ] = `${datalayerProperties?.NEXT_PUBLIC_PAGE_BREAKPT}${screenCategory} - ${window.innerWidth}w: ${deviceOrientation}`;
}

/**
 * Client side datalayer call to update specific attributes of datalayer
 * @param dataLayer | datalayer object
 * @param datalayerProperties | datalayer properties received from page context
 */
export function updateDatalayerObjForAdvSearch(dataLayer: Record<string, unknown>, datalayerProperties: any) {
  const screenCategory = getScreenCategory();
  const deviceOrientation = datalayerProperties?.NEXT_PUBLIC_ORIENTATION_LANDSCAPE;
  const tabSelected = localStorage?.getItem('advanceSearchPageTab');
  const page_url_query_string = dataLayer['page_url_query_string'];

  dataLayer[
    'page_breakpt_orientation'
  ] = `${datalayerProperties?.NEXT_PUBLIC_PAGE_BREAKPT}${screenCategory} - ${window?.innerWidth}w: ${deviceOrientation}`;
  dataLayer['ram_tile_names'] = datalayerProperties?.NEXT_PUBLIC_RAM_TILE_NAME;
  dataLayer['page_type'] = window?.location?.href;
  dataLayer['ensightenEnabled'] = true;
  dataLayer['page_data_layer_ready'] = true;
  delete dataLayer['env_api_tile_error_info'];
  delete dataLayer['page_url_query_string'];
  delete dataLayer['invokeFlushErrors'];
  delete dataLayer['search_currency_type'];
  if (tabSelected === 'Meetings') {
    dataLayer['page_url_query_string'] = page_url_query_string;
  }
  dataLayer['previous_page'] = window?.document?.referrer;
  dataLayer['isRewardRedemption'] = dataLayer['search_is_rewards_redemption'];
  delete dataLayer['ue'];
  delete dataLayer['res_context'];
  delete dataLayer['search_availability_search'];
  delete dataLayer['search_criteria_changed'];
  delete dataLayer['search_date_type'];
  /**
   * Remove the attributes if they are null or empty
   */
  const deleteIfNullOrEmpty = (key: string) => {
    if (dataLayer[key] === null || dataLayer[key] === 'null' || dataLayer[key] === '') {
      delete dataLayer[key];
    }
  };

  deleteIfNullOrEmpty('mr_prof_address_state');
  deleteIfNullOrEmpty('promoIDsEligible');
  deleteIfNullOrEmpty('mr_id_alternate');
  deleteIfNullOrEmpty('mr_prof_address_state_abbr');
  deleteIfNullOrEmpty('promoIDsRegistered');
  deleteIfNullOrEmpty('promo_special_offer_id');
  deleteIfNullOrEmpty('search_is_weekend_stay');
  deleteIfNullOrEmpty('search_date_check_in_day_of_week');
}

export const removePeriods = (date: string, replaceAll?: boolean) => {
  if (date) {
    if (replaceAll) {
      return date?.replaceAll('.', '');
    } else {
      return date?.replace('.,', ',');
    }
  } else {
    return '';
  }
};

export const getCustomFormattedDate = (date: any, currentLocale: string) => {
  switch (currentLocale) {
    case FR_LOCALE:
      return removePeriods(date, true);
    case DE_LOCALE:
      return removePeriods(date, false);
    case ES_LOCALE:
    case PT_BR_LOCALE:
    case RU_LOCALE:
      if (date?.charAt(0) !== ' ') {
        return date?.charAt(0)?.toUpperCase() + date?.slice(1);
      } else {
        return date?.charAt(1)?.toUpperCase() + date?.slice(2);
      }

    default:
      return date;
  }
};
// * Formats exact date range based on whether dates are in the same month/year
// * @param fromDate - Start date (moment object or string)
// * @param toDate - End date (moment object or string)
// * @param locale - Current locale string
// * @returns Formatted date string: "Mon D - D" if same month/year, "Mon D - Mon D" if different months
// *          (Mon = shortened month name like "Jan", "Feb", D = day without leading zeros)
// */
export const formatExactDateRange = (
  fromDate: DateObjectType | null | undefined,
  toDate: DateObjectType | null | undefined,
  locale: string | undefined
): string => {
  if (!fromDate || !toDate) {
    return '';
  }
  // Ensure dates are moment objects (handle both moment objects and strings)
  const fromMoment = moment(fromDate);
  const toMoment = moment(toDate);
  const isSameMonthYear = fromMoment.month() === toMoment.month() && fromMoment.year() === toMoment.year();
  // Ensure dates are moment objects (handle both moment objects and strings)
  const isChineseLocale = isCNLocale(locale || '') || isGreaterChinaLocale(locale || '');
  const dayFormat = isChineseLocale ? 'Do' : 'D';
  const monthDayFormat = isChineseLocale ? 'MMM Do' : 'MMM D';

  // If dates are in the same month/year, format as "Mon D - D"// Format: "Mon D - D" (e.g., "Jan 15 - 20" or "Feb 3 - 4" or "2月 5日 - 6日")
  if (isSameMonthYear) {
    const startDateFormatted = getCustomFormattedDate(getLocalizedDate(fromDate, monthDayFormat, locale), locale || '');
    const endDay = getLocalizedDate(toDate, dayFormat, locale);
    return `${startDateFormatted} - ${endDay}`;
  }

  const startDateFormatted = getCustomFormattedDate(getLocalizedDate(fromDate, monthDayFormat, locale), locale || '');
  const endDateFormatted = getCustomFormattedDate(getLocalizedDate(toDate, monthDayFormat, locale), locale || '');
  return `${startDateFormatted} - ${endDateFormatted}`;
};

//to convert the rates to the selected currency exchange rate
export const getExchangeConversionRate = (rate: number, exchangeRate: number) => {
  return Math.floor(rate * exchangeRate);
};

//to convert the rate to decimal point
export const createDecimalValue = (value: number, decimalPoint: number): number => {
  return Number((value / Math.pow(10, decimalPoint)).toFixed(decimalPoint));
};

export function deepCloneZustandState(state: Record<string, any>) {
  const clone: Record<string, any> = {};

  for (const [key, value] of Object.entries(state)) {
    if (typeof value === 'function') {
      // Copy function reference as-is
      clone[key] = value;
    } else if (value && typeof value === 'object') {
      // Deep clone objects/arrays
      clone[key] = JSON.parse(JSON.stringify(value));
    } else {
      // Copy primitives as-is
      clone[key] = value;
    }
  }

  return clone;
}

/**
 * Function to determine if the search comes under exclusion criteria or not
 * Exclusion Criteria -
 * i) Non-Lat/Long Searches (i.e., if lat/long is missing)
 * ii) searchDestinationType = Point of Interest/Address/Airport/Hotel Name
 * @param latitude | latitude value
 * @param destinationType | destinationType property from session or geocoder call
 * @param flexibleDate | flexibleDate property from session
 */
export const determineExclusionCriteriaForSortOptions = (
  latitude: string | number | undefined,
  destinationType: string,
  flexibleDate: boolean
) => {
  if (!latitude || EXCLUSION_CRITERIA_DESTINATION_TYPE.includes(destinationType) || flexibleDate) {
    return true;
  }
  return false;
};

export const checkIfSOOEnabled = (featureFlagSOO: { experience_mcom_shop_enable_soo: boolean }) => {
  return featureFlagSOO?.experience_mcom_shop_enable_soo || false;
};

export const checkIfGeographicSearchEnabled = (featureFlagSerpEnableGeographicSearch: {
  experience_mcom_enable_geographic_search: boolean;
}) => {
  return featureFlagSerpEnableGeographicSearch?.experience_mcom_enable_geographic_search || false;
};

export const isDestinationTypeFlagEnabled = (GET_DESTINATION_TYPE_FLAG: string | boolean) => {
  return GET_DESTINATION_TYPE_FLAG === 'true' || GET_DESTINATION_TYPE_FLAG === true;
};

export const isFlexibleDate = (availabilityRequestVO: {
  flexibleDate?: boolean | string;
  checkInDate?: any;
  checkOutDate?: any;
}) => {
  if (
    availabilityRequestVO?.flexibleDate === true ||
    availabilityRequestVO?.flexibleDate === 'true' ||
    !(availabilityRequestVO?.checkInDate && availabilityRequestVO?.checkOutDate)
  ) {
    return true;
  } else {
    return false;
  }
};

// Mapping the warning code according to the response
export const warningUtils = {
  findWarningIndex: (response: any, code: string | number): number =>
    response?.status?.[0]?.warnings?.findIndex((warning: any) => warning?.code === code),
  hasWarningCode: (response: any, codes: (string | number)[]): boolean =>
    response?.status?.[0]?.warnings?.some((warning: any) => codes.includes(warning.code)),
};

// Get formatted opening soon date
export const formatOpeningSoonDate = (
  openingSoonLabel: string | undefined,
  openingDate: DateObjectType | null | undefined,
  openingSoonDateFormat?: string,
  currentLocale?: string
) => {
  let formattedDate = getLocalizedDate(openingDate, openingSoonDateFormat, currentLocale);
  switch (currentLocale) {
    case 'de-DE':
    case 'ru-RU':
    case 'fr-FR':
      formattedDate = formattedDate.replace('.', '');
      return `${openingSoonLabel} ${formattedDate}`;
    case 'ko-KR':
      return `${formattedDate} ${openingSoonLabel}`;
    default:
      return `${openingSoonLabel} ${formattedDate}`;
  }
};
